/** Muscle catalog + recovery half-lives (hours).
 * Half-lives are product estimates inspired by published recovery windows
 * (~48-72h hard lower-body; often shorter for smaller groups): Thomas et al. 2024
 * (PubMed 38689583), Machado et al. 2019 (PMC6719818). Large lower body ~72h,
 * hips ~48h, small ~36h - not measured per-muscle constants.
 */
export type MuscleId =
  | 'rectus_femoris_l'
  | 'rectus_femoris_r'
  | 'vastus_lateralis_l'
  | 'vastus_lateralis_r'
  | 'vastus_medialis_l'
  | 'vastus_medialis_r'
  | 'vastus_intermedius_l'
  | 'vastus_intermedius_r'
  | 'gluteus_medius_l'
  | 'gluteus_medius_r'
  | 'gluteus_minimus_l'
  | 'gluteus_minimus_r'
  | 'tfl_l'
  | 'tfl_r'
  | 'gluteus_maximus_l'
  | 'gluteus_maximus_r'
  | 'hamstrings_l'
  | 'hamstrings_r'
  | 'calves_l'
  | 'calves_r'
  | 'adductors_l'
  | 'adductors_r'
  | 'chest'
  | 'lats_l'
  | 'lats_r'
  | 'traps_l'
  | 'traps_r'
  | 'delts_l'
  | 'delts_r'
  | 'biceps_l'
  | 'biceps_r'
  | 'triceps_l'
  | 'triceps_r'
  | 'forearms_l'
  | 'forearms_r'
  | 'abs'
  | 'obliques_l'
  | 'obliques_r'
  | 'lower_back'

export interface MuscleDef {
  id: MuscleId
  name: string
  group: string
  /** Exponential recovery half-life in hours */
  halfLifeHours: number
  side: 'left' | 'right' | 'center'
}

export const MUSCLES: Record<MuscleId, MuscleDef> = {
  rectus_femoris_l: { id: 'rectus_femoris_l', name: 'Rectus femoris (L)', group: 'Quadriceps', halfLifeHours: 72, side: 'left' },
  rectus_femoris_r: { id: 'rectus_femoris_r', name: 'Rectus femoris (R)', group: 'Quadriceps', halfLifeHours: 72, side: 'right' },
  vastus_lateralis_l: { id: 'vastus_lateralis_l', name: 'Vastus lateralis (L)', group: 'Quadriceps', halfLifeHours: 72, side: 'left' },
  vastus_lateralis_r: { id: 'vastus_lateralis_r', name: 'Vastus lateralis (R)', group: 'Quadriceps', halfLifeHours: 72, side: 'right' },
  vastus_medialis_l: { id: 'vastus_medialis_l', name: 'Vastus medialis (L)', group: 'Quadriceps', halfLifeHours: 72, side: 'left' },
  vastus_medialis_r: { id: 'vastus_medialis_r', name: 'Vastus medialis (R)', group: 'Quadriceps', halfLifeHours: 72, side: 'right' },
  vastus_intermedius_l: { id: 'vastus_intermedius_l', name: 'Vastus intermedius (L)', group: 'Quadriceps', halfLifeHours: 72, side: 'left' },
  vastus_intermedius_r: { id: 'vastus_intermedius_r', name: 'Vastus intermedius (R)', group: 'Quadriceps', halfLifeHours: 72, side: 'right' },
  gluteus_medius_l: { id: 'gluteus_medius_l', name: 'Gluteus medius (L)', group: 'Hip abductors', halfLifeHours: 48, side: 'left' },
  gluteus_medius_r: { id: 'gluteus_medius_r', name: 'Gluteus medius (R)', group: 'Hip abductors', halfLifeHours: 48, side: 'right' },
  gluteus_minimus_l: { id: 'gluteus_minimus_l', name: 'Gluteus minimus (L)', group: 'Hip abductors', halfLifeHours: 48, side: 'left' },
  gluteus_minimus_r: { id: 'gluteus_minimus_r', name: 'Gluteus minimus (R)', group: 'Hip abductors', halfLifeHours: 48, side: 'right' },
  tfl_l: { id: 'tfl_l', name: 'Tensor fasciae latae (L)', group: 'Hip abductors', halfLifeHours: 36, side: 'left' },
  tfl_r: { id: 'tfl_r', name: 'Tensor fasciae latae (R)', group: 'Hip abductors', halfLifeHours: 36, side: 'right' },
  gluteus_maximus_l: { id: 'gluteus_maximus_l', name: 'Gluteus maximus (L)', group: 'Glutes', halfLifeHours: 48, side: 'left' },
  gluteus_maximus_r: { id: 'gluteus_maximus_r', name: 'Gluteus maximus (R)', group: 'Glutes', halfLifeHours: 48, side: 'right' },
  hamstrings_l: { id: 'hamstrings_l', name: 'Hamstrings (L)', group: 'Hamstrings', halfLifeHours: 60, side: 'left' },
  hamstrings_r: { id: 'hamstrings_r', name: 'Hamstrings (R)', group: 'Hamstrings', halfLifeHours: 60, side: 'right' },
  calves_l: { id: 'calves_l', name: 'Calves (L)', group: 'Calves', halfLifeHours: 36, side: 'left' },
  calves_r: { id: 'calves_r', name: 'Calves (R)', group: 'Calves', halfLifeHours: 36, side: 'right' },
  adductors_l: { id: 'adductors_l', name: 'Adductors (L)', group: 'Adductors', halfLifeHours: 48, side: 'left' },
  adductors_r: { id: 'adductors_r', name: 'Adductors (R)', group: 'Adductors', halfLifeHours: 48, side: 'right' },
  chest: { id: 'chest', name: 'Chest', group: 'Chest', halfLifeHours: 48, side: 'center' },
  lats_l: { id: 'lats_l', name: 'Latissimus (L)', group: 'Back', halfLifeHours: 72, side: 'left' },
  lats_r: { id: 'lats_r', name: 'Latissimus (R)', group: 'Back', halfLifeHours: 72, side: 'right' },
  traps_l: { id: 'traps_l', name: 'Trapezius (L)', group: 'Back', halfLifeHours: 48, side: 'left' },
  traps_r: { id: 'traps_r', name: 'Trapezius (R)', group: 'Back', halfLifeHours: 48, side: 'right' },
  delts_l: { id: 'delts_l', name: 'Deltoids (L)', group: 'Shoulders', halfLifeHours: 48, side: 'left' },
  delts_r: { id: 'delts_r', name: 'Deltoids (R)', group: 'Shoulders', halfLifeHours: 48, side: 'right' },
  biceps_l: { id: 'biceps_l', name: 'Biceps (L)', group: 'Arms', halfLifeHours: 36, side: 'left' },
  biceps_r: { id: 'biceps_r', name: 'Biceps (R)', group: 'Arms', halfLifeHours: 36, side: 'right' },
  triceps_l: { id: 'triceps_l', name: 'Triceps (L)', group: 'Arms', halfLifeHours: 36, side: 'left' },
  triceps_r: { id: 'triceps_r', name: 'Triceps (R)', group: 'Arms', halfLifeHours: 36, side: 'right' },
  forearms_l: { id: 'forearms_l', name: 'Forearms (L)', group: 'Arms', halfLifeHours: 24, side: 'left' },
  forearms_r: { id: 'forearms_r', name: 'Forearms (R)', group: 'Arms', halfLifeHours: 24, side: 'right' },
  abs: { id: 'abs', name: 'Abs', group: 'Core', halfLifeHours: 24, side: 'center' },
  obliques_l: { id: 'obliques_l', name: 'Obliques (L)', group: 'Core', halfLifeHours: 24, side: 'left' },
  obliques_r: { id: 'obliques_r', name: 'Obliques (R)', group: 'Core', halfLifeHours: 24, side: 'right' },
  lower_back: { id: 'lower_back', name: 'Lower back', group: 'Back', halfLifeHours: 48, side: 'center' },
}

export const MUSCLE_LIST = Object.values(MUSCLES)

/** Visual mesh keys used by the 3D figure (aggregates fine muscles). */
export type MeshMuscleId =
  | 'quads_l'
  | 'quads_r'
  | 'hip_abd_l'
  | 'hip_abd_r'
  | 'glute_max_l'
  | 'glute_max_r'
  | 'hams_l'
  | 'hams_r'
  | 'calves_l'
  | 'calves_r'
  | 'adductors_l'
  | 'adductors_r'
  | 'chest'
  | 'lats_l'
  | 'lats_r'
  | 'traps_l'
  | 'traps_r'
  | 'delts_l'
  | 'delts_r'
  | 'biceps_l'
  | 'biceps_r'
  | 'triceps_l'
  | 'triceps_r'
  | 'forearms_l'
  | 'forearms_r'
  | 'abs'
  | 'obliques_l'
  | 'obliques_r'
  | 'lower_back'
  | 'torso'

export const MESH_TO_MUSCLES: Record<MeshMuscleId, MuscleId[]> = {
  quads_l: ['rectus_femoris_l', 'vastus_lateralis_l', 'vastus_medialis_l', 'vastus_intermedius_l'],
  quads_r: ['rectus_femoris_r', 'vastus_lateralis_r', 'vastus_medialis_r', 'vastus_intermedius_r'],
  hip_abd_l: ['gluteus_medius_l', 'gluteus_minimus_l', 'tfl_l'],
  hip_abd_r: ['gluteus_medius_r', 'gluteus_minimus_r', 'tfl_r'],
  glute_max_l: ['gluteus_maximus_l'],
  glute_max_r: ['gluteus_maximus_r'],
  hams_l: ['hamstrings_l'],
  hams_r: ['hamstrings_r'],
  calves_l: ['calves_l'],
  calves_r: ['calves_r'],
  adductors_l: ['adductors_l'],
  adductors_r: ['adductors_r'],
  chest: ['chest'],
  lats_l: ['lats_l'],
  lats_r: ['lats_r'],
  traps_l: ['traps_l'],
  traps_r: ['traps_r'],
  delts_l: ['delts_l'],
  delts_r: ['delts_r'],
  biceps_l: ['biceps_l'],
  biceps_r: ['biceps_r'],
  triceps_l: ['triceps_l'],
  triceps_r: ['triceps_r'],
  forearms_l: ['forearms_l'],
  forearms_r: ['forearms_r'],
  abs: ['abs'],
  obliques_l: ['obliques_l'],
  obliques_r: ['obliques_r'],
  lower_back: ['lower_back'],
  torso: [],
}

export const MESH_LABELS: Record<MeshMuscleId, string> = {
  quads_l: 'Left quadriceps',
  quads_r: 'Right quadriceps',
  hip_abd_l: 'Left hip abductors',
  hip_abd_r: 'Right hip abductors',
  glute_max_l: 'Left glute max',
  glute_max_r: 'Right glute max',
  hams_l: 'Left hamstrings',
  hams_r: 'Right hamstrings',
  calves_l: 'Left calves',
  calves_r: 'Right calves',
  adductors_l: 'Left adductors',
  adductors_r: 'Right adductors',
  chest: 'Chest',
  lats_l: 'Left lats',
  lats_r: 'Right lats',
  traps_l: 'Left traps',
  traps_r: 'Right traps',
  delts_l: 'Left delts',
  delts_r: 'Right delts',
  biceps_l: 'Left biceps',
  biceps_r: 'Right biceps',
  triceps_l: 'Left triceps',
  triceps_r: 'Right triceps',
  forearms_l: 'Left forearms',
  forearms_r: 'Right forearms',
  abs: 'Abs',
  obliques_l: 'Left obliques',
  obliques_r: 'Right obliques',
  lower_back: 'Lower back',
  torso: 'Torso',
}
